import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDeleteModal } from "../../../../hooks/shared/useDeleteModal";
import { filterRowsByQuery } from "../../../../utils/shared/searchHelpers";
import { formatValidationErrors } from "../../utils/adminApiHelpers";
import {
  buildProductFormData,
  buildProductFormDataForUpdate,
  createProduct,
  deleteProduct,
  fetchCategoriesList,
  fetchProductById,
  fetchProductList,
  unwrapList,
  unwrapOne,
  updateProduct,
} from "../api";
import { EMPTY_PRODUCT_FORM } from "../constants/emptyDefaults";
import { getProductColumns } from "../constants/productColumns";
import { getProductFields } from "../constants/productFields";
import { normalizeCategoryRow } from "../utils/normalizeCategory";
import { normalizeProductItem } from "../utils/normalizeProduct";

export function useManageProducts() {
  const { t } = useTranslation("dashboard");
  const tk = useCallback((key) => t(`manageProducts.${key}`), [t]);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const deleteModal = useDeleteModal();
  const [editingItem, setEditingItem] = useState(null);
  const [newFormKey, setNewFormKey] = useState(0);
  const [submitError, setSubmitError] = useState(null);

  const editingId = editingItem?.id;

  const { data: categories = [] } = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: async () => {
      const res = await fetchCategoriesList();
      return unwrapList(res).map(normalizeCategoryRow).filter(Boolean);
    },
  });

  const { data: editingDetail, isFetching: isEditLoading } = useQuery({
    queryKey: ["admin", "products", "detail", editingId],
    queryFn: async () => {
      const res = await fetchProductById(editingId);
      const raw = unwrapOne(res);
      return normalizeProductItem(raw) ?? normalizeProductItem(raw?.data);
    },
    enabled: editingId != null,
  });

  const formDefaults = useMemo(() => {
    if (!editingItem) return { ...EMPTY_PRODUCT_FORM };
    const s = editingDetail ?? editingItem;
    return {
      name_ar: s.name_ar ?? "",
      name_en: s.name_en ?? "",
      category_id: s.category_id != null ? String(s.category_id) : "",
      description_ar: s.description_ar ?? "",
      description_en: s.description_en ?? "",
      is_featured: Boolean(s.is_featured),
    };
  }, [editingItem, editingDetail]);

  const fields = useMemo(
    () => getProductFields(tk, categories),
    [tk, categories]
  );
  const columns = useMemo(() => getProductColumns(tk), [tk]);

  const {
    data: rows = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin", "products"],
    queryFn: async () => {
      const res = await fetchProductList();
      return unwrapList(res).map(normalizeProductItem).filter(Boolean);
    },
  });

  const createMutation = useMutation({
    mutationFn: (fd) => createProduct(fd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) => updateProduct(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
    },
  });

  const handleSubmit = async (data) => {
    setSubmitError(null);
    const payload = {
      ...data,
      is_featured: Boolean(data.is_featured),
    };
    const fd = editingItem
      ? buildProductFormDataForUpdate(payload)
      : buildProductFormData(payload);
    try {
      if (editingItem) {
        await updateMutation.mutateAsync({ id: editingItem.id, formData: fd });
        setEditingItem(null);
      } else {
        await createMutation.mutateAsync(fd);
      }
      setNewFormKey((k) => k + 1);
    } catch (err) {
      setSubmitError(formatValidationErrors(err));
    }
  };

  const confirmDelete = async () => {
    if (deleteModal.deleteId == null) return;
    try {
      await deleteMutation.mutateAsync(deleteModal.deleteId);
    } finally {
      deleteModal.close();
    }
  };

  const filtered = useMemo(
    () =>
      filterRowsByQuery(rows, search, [
        "name_en",
        "name_ar",
        "category_label",
      ]),
    [rows, search]
  );

  const errorMessage =
    error?.response?.data?.message ||
    error?.message ||
    (isError ? tk("alerts.loadFailed") : null);

  const pending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return {
    tk,
    categories,
    search,
    setSearch,
    deleteModal,
    editingItem,
    setEditingItem,
    newFormKey,
    submitError,
    isEditLoading,
    formDefaults,
    fields,
    columns,
    filtered,
    isLoading,
    errorMessage,
    pending,
    handleSubmit,
    confirmDelete,
    handleEdit: setEditingItem,
    handleDeleteClick: deleteModal.open,
    cancelEdit: () => setEditingItem(null),
  };
}
